import React, { useState } from 'react';
import QRCode from 'qrcode';
import { BsPeople, BsFillHouseDoorFill } from 'react-icons/bs';
import QRGenerator from '../Scanning/QRGenerator';

const ProfileCard = ({ user }) => {
    const [showQRModal, setShowQRModal] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [isGeneratingBulk, setIsGeneratingBulk] = useState(false);

    const handleGenerateQR = (profileId, profileName, thaaliNo) => {
        setSelectedProfile({ id: profileId, name: profileName, thaaliNo });
        setShowQRModal(true);
    };

    // Function to generate QR code data URL
    const generateQRCodeDataUrl = async (profileId) => {
        const qrOptions = {
            errorCorrectionLevel: 'H',
            type: 'image/png',
            quality: 0.92,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            },
            width: 256
        };

        try {
            return await QRCode.toDataURL(profileId, qrOptions);
        } catch (error) {
            console.error('Error generating QR for', profileId, error);
            return null;
        }
    };

    // Function to create single sticker HTML
    const createStickerHTML = (user, qrDataUrl) => {
        return `
            <div class="sticker-container">
                <!-- Left Section: Thaali Number -->
                <div class="left-section">
                    <div class="thaali-number">${user.thaaliNo || 'N/A'}</div>
                    <div class="organization-name">FMB - Istifadah Ilmiyah</div>
                </div>
                
                <!-- Center Section: QR Code -->
                <div class="center-section">
                    <img src="${qrDataUrl}" alt="QR Code" class="qr-image" />
                </div>
                
                <!-- Right Section: Profile ID and Name -->
                <div class="right-section">
                    <div class="profile-id">${user.hof}</div>
                    ${user.name ? `<div class="profile-name">${user.name}</div>` : ''}
                </div>
            </div>
        `;
    };

    // Function to fetch all users and generate bulk print
    const handlePrintAll = async () => {
        setIsGeneratingBulk(true);
        
        try {
            // Fetch all users from backend
            const response = await fetch(process.env.REACT_APP_API_URL + '/getAllUsers', {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }

            const data = await response.json();
            const users = data.users || data.data || data; // Handle different response formats

            if (!users || users.length === 0) {
                alert('No users found');
                return;
            }

            // Generate QR codes for all users
            const stickerPromises = users.map(async (userData) => {
                const qrDataUrl = await generateQRCodeDataUrl(userData.hof);
                if (qrDataUrl) {
                    return createStickerHTML(userData, qrDataUrl);
                }
                return null;
            });

            const stickers = await Promise.all(stickerPromises);
            const validStickers = stickers.filter(sticker => sticker !== null);

            if (validStickers.length === 0) {
                alert('Failed to generate QR codes');
                return;
            }

            // Create print content with all stickers
            const printContent = `
                <!DOCTYPE html>
                <html>
                    <head>
                        <title>All QR Stickers - Bulk Print</title>
                        <style>
                            * {
                                margin: 0;
                                padding: 0;
                                box-sizing: border-box;
                            }
                            
                            @page {
                                size: 10in 1in;
                                margin: 0;
                            }
                            
                            body {
                                font-family: 'Arial', sans-serif;
                                background: white;
                                margin: 0;
                                padding: 0;
                            }
                            
                            .sticker-container {
                                width: 10in;
                                height: 1in;
                                display: flex;
                                align-items: center;
                                justify-content: space-between;
                                border: 1px solid #C38154;
                                border-radius: 0.05in;
                                background: white;
                                padding: 0.05in;
                                position: relative;
                                box-sizing: border-box;
                                page-break-after: always;
                                margin-bottom: 0.1in;
                            }
                            
                            .sticker-container:last-child {
                                page-break-after: avoid;
                            }
                            
                            .left-section {
                                width: 2.5in;
                                height: 100%;
                                display: flex;
                                flex-direction: column;
                                justify-content: center;
                                align-items: center;
                                text-align: center;
                            }
                            
                            .thaali-number {
                                font-family: 'Arial', sans-serif;
                                font-size: 24px;
                                font-weight: bold;
                                color: #C38154;
                                background: #f8f9fa;
                                border: 2px solid #C38154;
                                border-radius: 0.05in;
                                padding: 0.05in 0.1in;
                                letter-spacing: 1px;
                                margin-bottom: 0.02in;
                            }
                            
                            .organization-name {
                                font-size: 8px;
                                font-weight: bold;
                                color: #C38154;
                                line-height: 1;
                            }
                            
                            .center-section {
                                height: 100%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                flex-shrink: 0;
                            }
                            
                            .qr-image {
                                width: 0.8in;
                                height: 0.8in;
                                border: 1px solid #ddd;
                                border-radius: 0.02in;
                                background: white;
                            }
                            
                            .right-section {
                                width: 2.5in;
                                height: 100%;
                                display: flex;
                                flex-direction: column;
                                justify-content: center;
                                align-items: center;
                                text-align: center;
                            }
                            
                            .profile-id {
                                font-family: 'Arial', sans-serif;
                                font-size: 16px;
                                font-weight: bold;
                                color: #C38154;
                                background: #f8f9fa;
                                border: 1px solid #C38154;
                                border-radius: 0.05in;
                                padding: 0.05in 0.08in;
                                letter-spacing: 0.5px;
                                margin-bottom: 0.02in;
                                word-wrap: break-word;
                                max-width: 100%;
                            }
                            
                            .profile-name {
                                font-size: 7px;
                                color: #666;
                                line-height: 1;
                                word-wrap: break-word;
                                max-width: 100%;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                            }
                            
                            @media print {
                                body {
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                }
                                
                                .sticker-container {
                                    border: 1px solid #C38154 !important;
                                    background: white !important;
                                }
                                
                                .thaali-number {
                                    background: #f8f9fa !important;
                                    border: 2px solid #C38154 !important;
                                }
                                
                                .profile-id {
                                    background: #f8f9fa !important;
                                    border: 1px solid #C38154 !important;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        ${validStickers.join('')}
                        
                        <script>
                            window.onload = function() {
                                setTimeout(() => {
                                    window.print();
                                    window.onafterprint = function() {
                                        window.close();
                                    };
                                }, 1000);
                            };
                        </script>
                    </body>
                </html>
            `;

            // Open print window
            const printWindow = window.open('', '_blank');
            printWindow.document.write(printContent);
            printWindow.document.close();

        } catch (error) {
            console.error('Error generating bulk QR stickers:', error);
            alert('Failed to generate bulk QR stickers. Please try again.');
        } finally {
            setIsGeneratingBulk(false);
        }
    };

    const design = (
        <>
            <div className="card-container">
                <div className="top-left-image">
                    <img src="tiffin.png" alt="Image Icon" />
                </div>
                <div className="card-content">
                    <div className="name">{user.name.toLowerCase()}</div>
                    <div className="numbers">
                        <span>ITS : {user.hof}</span>
                        <span>|</span>
                        <span>Thaali No : {user.thaaliNo}</span>
                    </div>
                    <div className="small-numbers">
                        {user.block ? <div className="text">
                            <span style={{fontSize:'16px',fontWeight:'bold',color:'black'}}>
                                <BsFillHouseDoorFill /> {user.block} 
                            </span>
                            <p>Flat No</p>
                        </div> : null}
                        {user.members ? <div className="text">
                            <span style={{fontSize:'16px',fontWeight:'bold',color:'black'}}>
                                <BsPeople className='mx-1' /> {user.members ? user.members : user.tiffinSize} 
                            </span>
                            <p>Members</p>
                        </div> : null}
                    </div>
                    
                    {/* Individual QR Button */}
                    {user?.role === "admin" && <button 
                        className="generate-qr-btn"
                        onClick={() => handleGenerateQR(user?.hof, user?.name, user?.thaaliNo)}
                    >
                        🔗 Show QR Code
                    </button>}

                    {/* Bulk Print Button */}
                    {/* <button 
                        className="generate-qr-btn"
                        onClick={handlePrintAll}
                        disabled={isGeneratingBulk}
                        style={{
                            marginTop: '10px',
                            backgroundColor: isGeneratingBulk ? '#ccc' : '#28a745',
                            cursor: isGeneratingBulk ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isGeneratingBulk ? '🔄 Generating...' : '🖨️ Print All QR Stickers'}
                    </button> */}

                    {/* QR Generator Modal */}
                    <QRGenerator
                        profileId={selectedProfile?.id}
                        profileName={selectedProfile?.name}
                        thaaliNo={selectedProfile?.thaaliNo}
                        isOpen={showQRModal}
                        onClose={() => setShowQRModal(false)}
                    />
                </div>
            </div>
        </>
    )
    return design;
}

export default ProfileCard;